import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Card, PillButton, Input, Modal, EmptyState } from '../../ui';
import { ReactComponent as UsersIcon } from '../../assets/icons/users.svg';
import { ReactComponent as UserPlusIcon } from '../../assets/icons/user-plus.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/trash.svg';
import { ReactComponent as LinkIcon } from '../../assets/icons/link.svg';
import { ReactComponent as CopyIcon } from '../../assets/icons/copy.svg';
import './AdminLayout.css';

function statusBadge(status, t) {
  const tone = status === 'active' ? 'success' : status === 'suspended' ? 'danger' : 'warn';
  return <span className={`sgc-admin__badge sgc-admin__badge--${tone}`}>{t(`admin.members.status.${status}`, status)}</span>;
}

function Members() {
  const { t } = useTranslation();
  const { org, has, seatsUsed, reloadOrg, orgId } = useOutletContext();

  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [inviteDeptId, setInviteDeptId] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteLink, setInviteLink] = useState('');

  const [seatsEditing, setSeatsEditing] = useState(false);
  const [seatsValue, setSeatsValue] = useState(org?.seats || 1);

  const canManage = has('members.manage');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [mRes, dRes, rRes] = await Promise.all([
        api.get(`/org/${orgId}/members`),
        api.get(`/org/${orgId}/departments`),
        api.get(`/org/${orgId}/roles`),
      ]);
      setMembers(mRes.data.members || []);
      setDepartments(dRes.data.departments || []);
      setRoles(rRes.data.roles || []);
      if (canManage) {
        const iRes = await api.get(`/org/${orgId}/invitations`);
        setInvitations(iRes.data.invitations || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('errors.generic'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, canManage]);

  useEffect(() => { load(); }, [load]);

  const roleName = useCallback((id) => roles.find((r) => r.id === id)?.name || '—', [roles]);
  const deptName = useCallback((id) => departments.find((d) => d.id === id)?.name || t('admin.members.noDepartment'), [departments, t]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteSending(true);
    setInviteError('');
    setInviteLink('');
    try {
      const res = await api.post(`/org/${orgId}/invitations`, {
        email: inviteEmail.trim().toLowerCase(),
        roleId: inviteRoleId,
        departmentId: inviteDeptId || null,
      });
      const token = res.data?.invitation?.token;
      if (token) setInviteLink(`${window.location.origin}/invite/${token}`);
      setInviteEmail('');
      load();
      reloadOrg();
    } catch (err) {
      setInviteError(err.response?.data?.error || t('errors.generic'));
    } finally {
      setInviteSending(false);
    }
  };

  const updateMember = async (id, updates) => {
    try {
      await api.patch(`/org/${orgId}/members/${id}`, updates);
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('errors.generic'));
    }
  };

  const removeMember = async (id) => {
    if (!window.confirm(t('admin.members.confirmRemove'))) return;
    try {
      await api.delete(`/org/${orgId}/members/${id}`);
      load();
      reloadOrg();
    } catch (err) {
      setError(err.response?.data?.error || t('errors.generic'));
    }
  };

  const revokeInvitation = async (token) => {
    try {
      await api.delete(`/org/${orgId}/invitations/${token}`);
      load();
      reloadOrg();
    } catch (err) {
      setError(err.response?.data?.error || t('errors.generic'));
    }
  };

  const saveSeats = async () => {
    try {
      await api.patch(`/org/${orgId}`, { seats: parseInt(seatsValue, 10) });
      setSeatsEditing(false);
      reloadOrg();
    } catch (err) {
      setError(err.response?.data?.error || t('errors.generic'));
    }
  };

  const seatsExceeded = org && seatsUsed >= org.seats;
  const inviteFormValid = inviteEmail.trim() && inviteRoleId;

  const rows = useMemo(() => ([
    ...members.map((m) => ({ kind: 'member', ...m })),
    ...invitations.map((inv) => ({ kind: 'invitation', ...inv })),
  ]), [members, invitations]);

  if (loading) return <p>{t('common.loading')}</p>;

  return (
    <div>
      <div className="sgc-admin__section-header">
        <div>
          <h2 className="sgc-admin__section-title">{t('admin.tabs.members')}</h2>
          <p className="sgc-admin__muted">
            {t('admin.members.seatsUsed', { used: seatsUsed, total: org?.seats })}
            {canManage && (
              seatsEditing ? (
                <span className="sgc-admin__row-actions" style={{ marginLeft: 8 }}>
                  <input
                    type="number" min={1} max={10000} value={seatsValue}
                    onChange={(e) => setSeatsValue(e.target.value)}
                    className="sgc-admin__select" style={{ width: 72 }}
                  />
                  <PillButton size="sm" onClick={saveSeats}>{t('common.save')}</PillButton>
                  <PillButton size="sm" variant="secondary" onClick={() => setSeatsEditing(false)}>{t('common.cancel')}</PillButton>
                </span>
              ) : (
                <button type="button" className="sgc-admin__icon-btn" style={{ marginLeft: 8, textDecoration: 'underline' }} onClick={() => { setSeatsValue(org.seats); setSeatsEditing(true); }}>
                  {t('admin.members.editSeats')}
                </button>
              )
            )}
          </p>
        </div>
        {canManage && (
          <PillButton icon={UserPlusIcon} onClick={() => { setInviteOpen(true); setInviteError(''); setInviteLink(''); }}>
            {t('admin.members.invite')}
          </PillButton>
        )}
      </div>

      {seatsExceeded && canManage && (
        <p className="sgc-admin__error">{t('admin.members.seatsExceeded')}</p>
      )}
      {error && <p className="sgc-admin__error">{error}</p>}

      <Card padded={false}>
        {rows.length === 0 ? (
          <EmptyState icon={UsersIcon} title={t('admin.members.emptyTitle')} description={t('admin.members.emptyDescription')} />
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('admin.members.col.name')}</th>
                <th>{t('admin.members.col.role')}</th>
                <th>{t('admin.members.col.department')}</th>
                <th>{t('admin.members.col.status')}</th>
                <th>{t('admin.members.col.lastAccess')}</th>
                {canManage && <th>{t('common.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.kind}-${row.id || row.token}`}>
                  <td>
                    <strong>{row.name || row.email || row.invitedEmail || '—'}</strong>
                    {(row.email || row.invitedEmail) && row.name && (
                      <div className="sgc-admin__muted">{row.email || row.invitedEmail}</div>
                    )}
                  </td>
                  <td>
                    {row.kind === 'member' && canManage ? (
                      <select
                        className="sgc-admin__select"
                        value={row.roleId}
                        onChange={(e) => updateMember(row.id, { roleId: e.target.value })}
                      >
                        {roles.filter((r) => r.name !== 'owner').map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    ) : roleName(row.roleId)}
                  </td>
                  <td>
                    {row.kind === 'member' && canManage ? (
                      <select
                        className="sgc-admin__select"
                        value={row.departmentId || ''}
                        onChange={(e) => updateMember(row.id, { departmentId: e.target.value || null })}
                      >
                        <option value="">{t('admin.members.noDepartment')}</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    ) : deptName(row.departmentId)}
                  </td>
                  <td>
                    {row.kind === 'invitation'
                      ? <span className="sgc-admin__badge sgc-admin__badge--warn">{t('admin.members.status.pending')}</span>
                      : statusBadge(row.status, t)}
                  </td>
                  <td className="sgc-admin__muted">
                    {row.kind === 'member' && row.lastAccess ? new Date(row.lastAccess).toLocaleString() : '—'}
                  </td>
                  {canManage && (
                    <td>
                      <div className="sgc-admin__row-actions">
                        {row.kind === 'member' ? (
                          <>
                            <select
                              className="sgc-admin__select"
                              value={row.status}
                              onChange={(e) => updateMember(row.id, { status: e.target.value })}
                            >
                              <option value="active">{t('admin.members.status.active')}</option>
                              <option value="suspended">{t('admin.members.status.suspended')}</option>
                            </select>
                            <button type="button" className="sgc-admin__icon-btn sgc-admin__icon-btn--danger" onClick={() => removeMember(row.id)} title={t('common.remove')}>
                              <TrashIcon width={16} height={16} aria-hidden="true" />
                            </button>
                          </>
                        ) : (
                          <button type="button" className="sgc-admin__icon-btn sgc-admin__icon-btn--danger" onClick={() => revokeInvitation(row.token)} title={t('admin.members.revoke')}>
                            <TrashIcon width={16} height={16} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title={t('admin.members.invite')}>
        <form className="sgc-admin__form" onSubmit={handleInvite}>
          <Input
            label={t('admin.members.col.name') + ' (email)'}
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
          />
          <select className="sgc-admin__select" value={inviteRoleId} onChange={(e) => setInviteRoleId(e.target.value)} required>
            <option value="">{t('admin.members.col.role')}</option>
            {roles.filter((r) => r.name !== 'owner').map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <select className="sgc-admin__select" value={inviteDeptId} onChange={(e) => setInviteDeptId(e.target.value)}>
            <option value="">{t('admin.members.noDepartment')}</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>

          {inviteError && <p className="sgc-admin__error">{inviteError}</p>}

          {inviteLink ? (
            <div>
              <p className="sgc-admin__muted">{t('admin.members.inviteSentEmail')}</p>
              <div className="sgc-admin__row-actions">
                <LinkIcon width={16} height={16} aria-hidden="true" />
                <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{inviteLink}</code>
                <button type="button" className="sgc-admin__icon-btn" onClick={() => navigator.clipboard.writeText(inviteLink)} title={t('dashboard.copy')}>
                  <CopyIcon width={14} height={14} aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : (
            <div className="sgc-admin__form-actions">
              <PillButton type="submit" disabled={!inviteFormValid || inviteSending}>
                {inviteSending ? t('common.loading') : t('admin.members.sendInvite')}
              </PillButton>
            </div>
          )}
        </form>
      </Modal>
    </div>
  );
}

export default Members;
